import React, { useState, useEffect, useRef } from "react";
import { navLinks, profileData } from "../data/mock";
import { Menu, X, Mail, Phone, Linkedin, Copy, Check, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className={`p-1 rounded transition-all ${
        copied ? "text-teal-600" : "text-slate-400 hover:text-slate-600"
      }`}
      title={copied ? "Copied!" : "Copy"}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    setDropdownOpen(false);
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const contactItems = [
    { icon: Mail, label: "Email", value: profileData.email, href: `mailto:${profileData.email}` },
    { icon: Phone, label: "Phone", value: profileData.phone, href: `tel:${profileData.phone}` },
    { icon: Linkedin, label: "LinkedIn", value: "linkedin.com/in/lpace1", href: profileData.linkedin, external: true },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="font-serif text-xl lg:text-2xl font-semibold tracking-tight text-slate-900 hover:text-teal-700 transition-colors"
          >
            LP
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-all"
              >
                {link.label}
              </a>
            ))}

            {/* Get in Touch dropdown */}
            <div className="relative ml-3" ref={dropdownRef}>
              <Button
                size="sm"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm flex items-center gap-1.5"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                Get in Touch
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                />
              </Button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-slate-100 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                  {contactItems.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors"
                    >
                      <item.icon size={15} className="text-teal-600 shrink-0" />
                      <a
                        href={item.href}
                        target={item.external ? "_blank" : undefined}
                        rel={item.external ? "noopener noreferrer" : undefined}
                        className="flex-1 min-w-0 text-sm text-slate-700 hover:text-teal-600 transition-colors truncate"
                      >
                        {item.value}
                      </a>
                      <CopyButton value={item.value} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 text-slate-700 hover:text-slate-900"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-slate-100 shadow-lg">
          <nav className="flex flex-col px-6 py-4 gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-4 py-3 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-all"
              >
                {link.label}
              </a>
            ))}
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
              <p className="px-4 text-xs text-slate-400 uppercase tracking-wide">Get in Touch</p>
              {contactItems.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <item.icon size={15} className="text-teal-600 shrink-0" />
                  <a
                    href={item.href}
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                    className="flex-1 min-w-0 text-sm text-slate-700 truncate"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.value}
                  </a>
                  <CopyButton value={item.value} />
                </div>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
